import React from "react";
import { getAttachmentFileObject, getAttachments, uploadAttachments, Attachment, EmlAttachment } from "../taskpane";
import { makeStyles, tokens } from "@fluentui/react-components";

interface Props {
  type: string;
  destination: string;
  onClose: () => void;
}

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  model: {
    position: "fixed",
    backgroundColor: tokens.colorNeutralBackground1,
    border: "solid 2px #000",
    top: "40%",
    left: "5%",
    width: "90%",
    boxSizing: "border-box",
    padding: "10px",
  },
  title: {
    marginTop: 0,
    marginBottom: "10px",
  },
  list: {
    listStyleType: "none",
    paddingLeft: 0,
    paddingBottom: "20px",
  },
  listItem: {
    display: "flex",
    alignItems: "start",
    marginBottom: "10px",
    gap: "5px",
  },
  checkbox: {
    padding: "2px",
  },
  actions: {
    display: "flex",
    justifyContent: "end",
    gap: "5px",
  },
  button: {
    backgroundColor: tokens.colorBrandBackground,
    color: "#fff",
    border: "solid 2px #000",
    padding: "5px 10px",
    cursor: "pointer",
    "&:disabled": {
      backgroundColor: tokens.colorNeutralBackground5 + " !important",
    },
    "&:hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  cancelButton: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: "#000",
    border: "solid 2px #000",
    padding: "5px 10px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
});

const UploadModel: React.FC<Props> = ({ type, destination, onClose }: Props) => {
  const [attachments, setAttachments] = React.useState<Attachment[]>([]);
  const [selectedAttachments, setSelectedAttachments] = React.useState<Attachment[]>([]);
  const [isUploading, setUploading] = React.useState(false);

  const styles = useStyles();

  React.useEffect(() => {
    getAttachments().then((attachments) => {
      const combined = [EmlAttachment, ...attachments];
      setAttachments(combined);
      setSelectedAttachments(combined);
    });
  }, []);

  function selectAttachment(attachment: Attachment) {
    if (!selectedAttachments.includes(attachment)) {
      setSelectedAttachments([...selectedAttachments, attachment]);
    } else {
      setSelectedAttachments(selectedAttachments.filter((a) => a !== attachment));
    }
  }

  // eslint-disable-next-line no-undef

  async function upload() {
    setUploading(true);

    try {
      const files = await Promise.all(
        selectedAttachments.map(async (attachment) => await getAttachmentFileObject(attachment))
      );
      await uploadAttachments(files, type, destination);
    } catch (e) {
      // eslint-disable-next-line no-undef
      console.error(e);
    } finally {
      setUploading(false);
      onClose();
    }
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.model}>
        <div>
          <h3 className={styles.title}>Upload to {destination}</h3>
        </div>
        <ul className={styles.list}>
          {attachments.map((attachment, index) => (
            <li className={styles.listItem} key={index}>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  onChange={() => selectAttachment(attachment)}
                  checked={selectedAttachments.includes(attachment)}
                />
              </div>
              <div>{attachment.name}</div>
            </li>
          ))}
        </ul>
        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.button} disabled={isUploading} onClick={upload}>
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModel;
